/**
 * P0-0c 浏览器与图形设置引导
 *
 * 两处职责：
 *  1. `buildGuidance()`：把能力报告翻译成**可执行步骤**（哪个浏览器、点哪里），
 *     而不是只说"不支持"。专业用户看到"不支持"会直接怀疑产品质量。
 *  2. `pickBrowserCandidate()`：给启动器用的候选浏览器优先级说明，
 *     与 `启动Pro.bat` 的实际探测顺序保持一致（改一处必须同步另一处，故在此写死引用）。
 *
 * 为什么是"指定浏览器"而不是"提示用户换内核"：
 *  360/QQ 浏览器的兼容模式（Trident 内核）由用户默认设置决定，
 *  而旧启动器 `start http://localhost:8000` 会顺着系统默认浏览器走 —— 用户什么都不做错，
 *  打开的却是一个没有 WebGL 的窗口。让启动器显式选 Edge/Chrome，从根上避免这次失败。
 */

import type { CapabilityReport } from './capability';

export interface GuidanceStep {
  /** 步骤序号文案，如 "第 1 步" */
  title: string;
  detail: string;
}

export interface Guidance {
  /** 标题，例如"3D 导演台暂时无法使用" */
  headline: string;
  /** 一句话说清影响范围，避免用户以为整个软件坏了 */
  scope: string;
  steps: GuidanceStep[];
  /** 兜底建议：都试过了怎么办 */
  fallback: string;
}

/** 启动器按此顺序挑选浏览器（与 bat 内实现严格一致） */
export const BROWSER_CANDIDATES = [
  { key: 'edge', label: 'Microsoft Edge', note: 'Win10/Win11 自带，始终为最新版' },
  { key: 'chrome', label: 'Google Chrome', note: '已安装则优先使用' },
  { key: 'default', label: '系统默认浏览器', note: '前两者都没有时的兜底，需自行确认内核' },
] as const;

export type BrowserCandidateKey = (typeof BROWSER_CANDIDATES)[number]['key'];

/** Chromium 系内核（Edge/Chrome/360极速/QQ浏览器极速模式）开启硬件加速的设置路径 */
const CHROMIUM_ACCEL_PATH =
  '地址栏输入 edge://settings/system（Chrome 为 chrome://settings/system）→ 打开「使用硬件加速（如可用）」→ 点「重新启动」';

export function buildGuidance(report: CapabilityReport, engine: string): Guidance {
  // 情形①：旧内核。最常见就是 360/QQ 的"兼容模式"
  if (!report.modernEngine) {
    return {
      headline: '当前浏览器为旧内核，无法运行 3D 导演台',
      scope: '画布、资产、剧本、生成等其他功能不受影响，仅「3D 导演台」需要现代浏览器内核。',
      steps: [
        {
          title: '第 1 步（推荐）',
          detail:
            '关闭本页面，改用桌面上的「智创短剧创作平台 Pro」启动图标重新打开——启动器会自动挑选 Edge 或 Chrome，不会再落到旧内核。',
        },
        {
          title: '第 2 步（若你仍想在当前浏览器里用）',
          detail:
            '在 360/QQ 浏览器地址栏右侧找到内核切换图标（通常为「e」或闪电标志），点选「极速模式」后刷新页面。',
        },
      ],
      fallback: '若切换后仍不可用，请直接用 Microsoft Edge 打开 http://localhost:8001 并告知我们你的浏览器版本。',
    };
  }

  // 情形②：拿不到 WebGL2 上下文
  if (!report.webgl2) {
    return {
      headline: '浏览器未能创建 WebGL2 图形环境',
      scope: '仅影响「3D 导演台」，其余功能可正常使用。',
      steps: [
        {
          title: '第 1 步：确认浏览器版本',
          detail: `当前识别为「${engine}」。请把浏览器升级到最新版后重试——旧版 Chromium（低于 52）不支持 WebGL2。`,
        },
        {
          title: '第 2 步：开启硬件加速',
          detail: CHROMIUM_ACCEL_PATH,
        },
      ],
      fallback:
        '若仍失败，多半是显卡驱动过旧。可用 Win+X → 设备管理器 → 显示适配器，更新驱动后重启浏览器。',
    };
  }

  // 情形③：WebGL2 有，但落到软件渲染 —— 不报错，只是极慢，最容易让用户困惑
  if (report.softwareRenderer) {
    return {
      headline: '当前由 CPU 软件模拟图形渲染，3D 预演会严重卡顿',
      scope: '功能可用但帧率不可接受，因此默认不进入导演台。其余功能不受影响。',
      steps: [
        {
          title: '第 1 步：开启硬件加速（最常见原因）',
          detail: CHROMIUM_ACCEL_PATH,
        },
        {
          title: '第 2 步：确认没有走远程/虚拟机环境',
          detail: '远程桌面、虚拟机、无显卡的服务器环境下浏览器会强制软件渲染，这类环境不适合做 3D 预演。',
        },
        {
          title: '第 3 步（可选）：仍想尝试',
          detail: '在「设置 → 3D 导演台」里手动放开本模块，画质会锁定在「流畅」档。',
        },
      ],
      fallback: '建议在配有集成显卡及以上的实机（Win10/Win11）上使用该功能。',
    };
  }

  return {
    headline: '3D 导演台可用',
    scope: `当前画质档位：${report.tier}。`,
    steps: [],
    fallback: '',
  };
}

/**
 * 供启动器/帮助页展示：我们推荐的浏览器次序与理由。
 * 与 BROWSER_CANDIDATES 同源，避免文案与实际逻辑漂移。
 */
export function browserRecommendation(): string[] {
  return BROWSER_CANDIDATES.map((c) => `${c.label} — ${c.note}`);
}
